import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarX } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ABSENCE_TYPES = [
  { value: 'maladie_enfant', label: 'Maladie enfant' },
  { value: 'conge_parent', label: 'Congé parent' },
  { value: 'autre', label: 'Autre absence' },
];

const AbsenceDialog = ({ isOpen, onClose, contractId, onAbsenceDeclared }) => {
  const [loading, setLoading] = useState(false);
  const [absenceType, setAbsenceType] = useState('maladie_enfant');
  const [notes, setNotes] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  
  const _today = new Date();
  const todayStr = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, '0')}-${String(_today.getDate()).padStart(2, '0')}`;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const startDate = dateDebut || todayStr;
    
    try {
      setLoading(true);
      const payload = {
        contract_id: contractId,
        date: startDate,
        absence_type: absenceType,
        notes: notes || null,
      };
      
      // Multi-day range
      if (dateFin && dateFin > startDate) {
        payload.date_fin = dateFin;
      }
      
      const response = await axios.post(`${API}/time/declare-absence`, payload);
      
      toast.success(response.data.message || 'Absence déclarée avec succès');
      setAbsenceType('maladie_enfant');
      setNotes('');
      setDateDebut('');
      setDateFin('');
      onClose();
      onAbsenceDeclared();
    } catch (error) {
      console.error('Error declaring absence:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la déclaration');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-primary)]" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="absence-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--warning)' }}>
              <CalendarX className="h-6 w-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-secondary)' }}>
                Déclarer une absence
              </DialogTitle>
              <DialogDescription>
                L'enfant sera marqué absent (0 heures, 0 indemnités)
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="date_debut">Date de début *</Label>
              <Input
                id="date_debut"
                type="date"
                value={dateDebut || todayStr}
                onChange={(e) => setDateDebut(e.target.value)}
                required
                data-testid="absence-date-debut"
              />
            </div>
            <div>
              <Label htmlFor="date_fin">Date de fin (optionnel)</Label>
              <Input
                id="date_fin"
                type="date"
                min={dateDebut || todayStr}
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                data-testid="absence-date-fin"
              />
              {dateFin && dateFin > (dateDebut || todayStr) && (
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  Une ligne d'absence par jour ouvrable
                </p>
              )}
            </div>
          </div>
          
          <div>
            <Label htmlFor="absence_type">Type d'absence *</Label>
            <Select value={absenceType} onValueChange={setAbsenceType} required>
              <SelectTrigger data-testid="absence-type-select">
                <SelectValue placeholder="Sélectionner le type" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-primary)]" style={{ backgroundColor: 'var(--bg-primary)', opacity: 1 }}>
                {ABSENCE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="notes">Notes complémentaires (optionnel)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Certificat médical fourni..."
              rows={3}
              data-testid="absence-notes-input"
            />
          </div>
          
          <div className="p-3 rounded-lg bg-[var(--neutral-50)] border border-[var(--border-light)]">
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              💡 Pour les <strong>congés payés</strong>, utilisez la page <a href="/conges" className="underline font-medium" style={{ color: 'var(--primary-400)' }}>Congés</a> qui gère le solde, les demandes et le calcul d'indemnités.
            </p>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              style={{ backgroundColor: 'var(--warning)' }}
              data-testid="submit-absence-button"
            >
              {loading ? 'Déclaration...' : 'Déclarer l\'absence'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AbsenceDialog;
